import { contactMailto, type ContactMessage } from './mail'

/**
 * Contact submission, ported from v1 (src/lib/contact.ts).
 *
 * With VITE_CONTACT_ENDPOINT set (v1 points it at a HighLevel inbound webhook), the message is POSTed
 * there as JSON: { firstName, lastName, email, message, website }. A 2xx is success; a non-2xx with
 * { "error": "..." } shows that sentence to the visitor as-is. With the variable unset, the visitor's
 * own mail app opens with the message laid out (lib/mail.ts).
 */
export const ENDPOINT: string = (import.meta.env.VITE_CONTACT_ENDPOINT ?? '').trim()

/** Zero-width and bidi marks: U+200B-U+200F, U+202A-U+202E, U+2060, U+FEFF. */
const isInvisible = (c: number) => (c >= 0x200b && c <= 0x200f) || (c >= 0x202a && c <= 0x202e) || c === 0x2060 || c === 0xfeff

/**
 * Drops control characters (U+0000-U+001F, U+007F) and zero-width / bidi marks. With `allowNewlines`,
 * tab, LF and CR survive. Checked by code point rather than a regex (no control characters in source).
 */
export function sanitize(input: string, allowNewlines = false): string {
  let out = ''
  for (const ch of input) {
    const c = ch.codePointAt(0)!
    const control = c <= 0x1f || c === 0x7f
    if (control && !(allowNewlines && (c === 0x09 || c === 0x0a || c === 0x0d))) continue
    if (isInvisible(c)) continue
    out += ch
  }
  return out
}

/** What the endpoint receives. `website` is the honeypot: empty for a person, so the backend can drop anything else. */
export type Lead = ContactMessage & { website: string }

export type SubmitResult = { via: 'webhook' } | { via: 'mailto' }

export class SubmitError extends Error {}

export function toLead(m: ContactMessage, website: string): Lead {
  return {
    firstName: sanitize(m.firstName.trim()),
    lastName: sanitize(m.lastName.trim()),
    email: sanitize(m.email.trim()),
    message: sanitize(m.message.trim(), true),
    website,
  }
}

export async function submitLead(lead: Lead): Promise<SubmitResult> {
  if (ENDPOINT) {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(lead),
    })
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null
      throw new SubmitError(body?.error || `The server answered ${res.status}.`)
    }
    return { via: 'webhook' }
  }
  window.open(contactMailto(lead), '_self') // window.open (not location) so the E2E check can read the URL
  return { via: 'mailto' }
}
